/**
 * DP / Folha — Fase 3.1: Motor de Folha Mensal CLT
 * Aba da página DPFolhaConfigPage
 *
 * Sub-tabs:
 *   - Rubricas (catálogo)
 *   - Competências (períodos de folha + cálculo)
 *   - Holerites (detalhamento por competência)
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateBR } from '../../lib/helpers';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { cn } from '../../lib/utils';
import {
  Calculator, Calendar, FileText, Plus, Trash2, Pencil, Play, Lock, Unlock,
  CheckCircle2, AlertCircle, Loader2, Eye, RefreshCw, TrendingUp, TrendingDown, Receipt,
  FileDown, FilePlus, Clock, Send, Check, ChevronsUpDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtBRL = (v) => v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = (v) => `${(Number(v) * 100).toFixed(1).replace('.', ',')}%`;
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const isHoraExtraFormula = (formulaTipo) => /^hora_extra_\d+(?:_\d+)?$/.test(String(formulaTipo || ''));
const getHoraExtraPercentual = (formulaTipo) => {
  if (!isHoraExtraFormula(formulaTipo)) return null;
  const bruto = String(formulaTipo).replace('hora_extra_', '').replace('_', '.');
  const percentual = Number(bruto);
  return Number.isFinite(percentual) ? percentual : null;
};

// ============================================================================
// MEMÓRIA DE CÁLCULO LEGÍVEL — traduz a memória técnica em demonstrativo
// para o analista de folha (base de cálculo, faixas, alíquotas, deduções).
// ============================================================================
const MemoriaLegivel = ({ memoria }) => {
  if (!memoria || typeof memoria !== 'object') return null;

  // INSS — tabela progressiva por faixas
  if (memoria.detalhe_faixas) {
    return (
      <div className="space-y-2">
        <p className="text-slate-600">
          Base de cálculo: <b>{fmtBRL(memoria.base_aplicada ?? memoria.base_recebida)}</b>
          {memoria.teto && Number(memoria.base_recebida) > Number(memoria.teto) && (
            <span className="text-amber-600"> (limitada ao teto de {fmtBRL(memoria.teto)})</span>
          )}
        </p>
        <p className="text-slate-500">O INSS é progressivo: cada parte do salário paga a alíquota da sua faixa.</p>
        <table className="w-full border rounded">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-1.5">Faixa salarial</th>
              <th className="text-right p-1.5">Alíquota</th>
              <th className="text-right p-1.5">Parte do salário na faixa</th>
              <th className="text-right p-1.5">Contribuição</th>
            </tr>
          </thead>
          <tbody>
            {memoria.detalhe_faixas.map((f, i) => (
              <tr key={i} className="border-t">
                <td className="p-1.5">R$ {f.faixa}</td>
                <td className="p-1.5 text-right">{fmtPct(f.aliquota)}</td>
                <td className="p-1.5 text-right">{fmtBRL(f.valor_na_faixa)}</td>
                <td className="p-1.5 text-right font-semibold text-red-700">{fmtBRL(f.contribuicao)}</td>
              </tr>
            ))}
            <tr className="border-t bg-slate-50 font-bold">
              <td colSpan={3} className="p-1.5 text-right">Total do desconto →</td>
              <td className="p-1.5 text-right text-red-700">
                {fmtBRL(memoria.detalhe_faixas.reduce((s, f) => s + (f.contribuicao || 0), 0))}
              </td>
            </tr>
          </tbody>
        </table>
        {memoria.fonte_legal && <p className="text-slate-400 italic">{memoria.fonte_legal}</p>}
      </div>
    );
  }

  if (memoria.tipo === 'salario_base' && memoria.base_marcada !== undefined) {
    return (
      <div className="space-y-2 text-slate-600">
        <p>Base bruta marcada para pensão: <b>{fmtBRL(memoria.base_marcada)}</b></p>
        <p>(-) INSS: <b>{fmtBRL(memoria.inss)}</b></p>
        <p>(-) IRRF: <b>{fmtBRL(memoria.irrf)}</b></p>
        <p className="border-t border-slate-200 pt-1">Base líquida da pensão: <b>{fmtBRL(memoria.base_calculo)}</b></p>
        <p>Percentual aplicado: <b>{fmtPct(Number(memoria.percentual || 0) / 100)}</b></p>
      </div>
    );
  }

  // IRRF — passo a passo da base + tabela progressiva por faixas
  if (memoria.detalhe_faixas_irrf || memoria.faixa || memoria.base_calculo !== undefined) {
    return (
      <div className="space-y-2">
        <p className="text-slate-600">Base original: <b>{fmtBRL(memoria.base_original)}</b></p>
        {memoria.desconto_inss !== undefined && memoria.desconto_inss > 0 && (
          <p className="text-slate-600">(-) INSS: <b>{fmtBRL(memoria.desconto_inss)}</b></p>
        )}
        {memoria.base_liquida !== undefined && (
          <p className="text-slate-600">= Base após INSS: <b>{fmtBRL(memoria.base_liquida)}</b></p>
        )}
        {memoria.deducao_dependentes !== undefined && (
          <p className="text-slate-600">(-) Dedução por dependentes: <b>{fmtBRL(memoria.deducao_dependentes)}</b></p>
        )}
        {memoria.desconto_simplificado !== undefined && (
          <p className="text-slate-600">(-) Desconto simplificado: <b>{fmtBRL(memoria.desconto_simplificado)}</b></p>
        )}
        {memoria.base_calculo !== undefined && <p className="text-slate-600">= Base de cálculo: <b>{fmtBRL(memoria.base_calculo)}</b></p>}
        {memoria.retencao_minima_aplicada && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
            <p>IRRF calculado no mês: <b>{fmtBRL(memoria.valor_irrf_calculado)}</b></p>
            {memoria.valor_pendente_anterior > 0 && <p>Saldo anterior acumulado: <b>{fmtBRL(memoria.valor_pendente_anterior)}</b></p>}
            {memoria.valor_pendente > 0
              ? <p>Retenção inferior a R$ 10,00. Saldo acumulado para o próximo mês: <b>{fmtBRL(memoria.valor_pendente)}</b></p>
              : <p>Valor acumulado atingiu o limite mínimo de R$ 10,00 e foi lançado.</p>}
          </div>
        )}

        {memoria.detalhe_faixas_irrf && memoria.detalhe_faixas_irrf.length > 0 && (
          <>
            <p className="text-slate-500 text-sm">O IRRF é progressivo: cada faixa de renda é tributada pela alíquota da sua faixa.</p>
            <table className="w-full border rounded">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-1.5">Faixa de renda</th>
                  <th className="text-right p-1.5">Alíquota</th>
                  <th className="text-right p-1.5">Base na faixa</th>
                  <th className="text-right p-1.5">Imposto</th>
                </tr>
              </thead>
              <tbody>
                {memoria.detalhe_faixas_irrf.map((f, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1.5">R$ {f.faixa}</td>
                    <td className="p-1.5 text-right">{fmtPct(f.aliquota)}</td>
                    <td className="p-1.5 text-right">{fmtBRL(f.base_na_faixa)}</td>
                    <td className="p-1.5 text-right font-semibold text-red-700">{fmtBRL(f.imposto_na_faixa)}</td>
                  </tr>
                ))}
                <tr className="border-t bg-slate-50 font-bold">
                  <td colSpan={3} className="p-1.5 text-right">Total do imposto →</td>
                  <td className="p-1.5 text-right text-red-700">
                    {fmtBRL(memoria.detalhe_faixas_irrf.reduce((s, f) => s + (f.imposto_na_faixa || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        {(!memoria.detalhe_faixas_irrf || memoria.detalhe_faixas_irrf.length === 0) && memoria.faixa && (
          <p className="bg-amber-50 border border-amber-100 rounded p-1.5 mt-1">
            Faixa aplicada: <b>R$ {memoria.faixa.intervalo}</b> → alíquota de <b>{fmtPct(memoria.faixa.aliquota)}</b>,
            menos parcela a deduzir de <b>{fmtBRL(memoria.faixa.deducao_legal)}</b>
          </p>
        )}

        {(memoria.irrf_tradicional !== undefined && memoria.irrf_simplificado !== undefined) && (
          <p className="text-slate-500 mt-1">Comparação: tradicional <b>{fmtBRL(memoria.irrf_tradicional)}</b> vs simplificado <b>{fmtBRL(memoria.irrf_simplificado)}</b> → aplicado o menor{memoria.modo_aplicado ? ` (${memoria.modo_aplicado})` : ""}.</p>
        )}
        {memoria.modo && <p className="text-slate-400 italic">Modo de apuração: {memoria.modo}</p>}
        {memoria.reducao_lei_15270 && (
          <p className="text-blue-600 text-sm">
            {memoria.reducao_lei_15270.regra === 'isencao_total'
              ? `✔ Isento (Lei 15.270/2025 — base ≤ ${fmtBRL(memoria.reducao_lei_15270.limite)})`
              : memoria.reducao_lei_15270.regra === 'reducao_parcial'
              ? `✔ Redução parcial (Lei 15.270/2025): redução de ${fmtBRL(memoria.reducao_lei_15270.reducao_aplicada)}`
              : ''}
          </p>
        )}
      </div>
    );
  }

  // Memórias de verbas variáveis seguem o mesmo demonstrativo resumido.
  if (memoria.adicional && memoria.horas !== undefined && memoria.valor_hora !== undefined) {
    return (
      <div className="space-y-2 text-slate-600">
        <p>Quantidade de horas: <b>{memoria.horas}</b></p>
        <p>Valor-hora: <b>{fmtBRL(memoria.valor_hora)}</b></p>
        <p>Adicional aplicado: <b>{memoria.adicional}</b></p>
        <p className="border-t border-slate-200 pt-1">Valor calculado: <b className="text-emerald-700">{fmtBRL(memoria.valor)}</b></p>
        {memoria.fonte && <p className="text-slate-400 italic">Fonte: {memoria.fonte}</p>}
      </div>
    );
  }

  if ((memoria.dias_repouso !== undefined || memoria.domingos_feriados !== undefined) && memoria.dias_uteis !== undefined && memoria.base !== undefined) {
    return (
      <div className="space-y-2 text-slate-600">
        <p>Base das horas extras: <b>{fmtBRL(memoria.base)}</b></p>
        <p>Dias de repouso, domingos e feriados: <b>{memoria.dias_repouso ?? memoria.domingos_feriados}</b></p>
        <p>Dias úteis: <b>{memoria.dias_uteis}</b></p>
        <p className="border-t border-slate-200 pt-1">Valor calculado do DSR: <b className="text-emerald-700">{fmtBRL(memoria.valor)}</b></p>
        {memoria.ajuste_competencia && <p className="text-slate-400 italic">Dias parametrizados na competência.</p>}
        {memoria.fonte && <p className="text-slate-400 italic">Fonte: {memoria.fonte}</p>}
      </div>
    );
  }

  if (memoria.tipo_beneficio && memoria.dependente_nome) {
    return (
      <div className="space-y-2 text-slate-600">
        <p>Dependente: <b>{memoria.dependente_nome}</b></p>
        <p>Benefício: <b>{String(memoria.tipo_beneficio).replaceAll('_', ' ')}</b></p>
        <p>Competência: <b>{String(memoria.competencia || '').replaceAll('_', ' ')}</b></p>
        <p className="border-t border-slate-200 pt-1">Valor descontado: <b className="text-red-700">{fmtBRL(memoria.valor)}</b></p>
      </div>
    );
  }

  // Memórias simples (avos do 13º, fórmulas, ponto, multa FGTS...) — frases legíveis
  const LABELS = {
    avos: 'Meses trabalhados (avos)', salario_base: 'Salário base',
    valor_integral: '13º integral', percentual: 'Percentual aplicado',
    formula: 'Fórmula', dias: 'Dias', saldo_fgts_depositado: 'Saldo FGTS depositado',
    motivo: 'Motivo', aliquota: 'Alíquota', base: 'Base de cálculo',
    horas: 'Horas', valor_hora: 'Valor-hora', adicional: 'Adicional', fonte: 'Fonte',
    composicao_base: 'Composição da base', verbas_isentas: 'Verbas isentas',
    inss_sobre_saldo: 'INSS sobre saldo de salário', inss_sobre_13: 'INSS sobre 13º proporcional',
    irrf_sobre_saldo: 'IRRF sobre saldo de salário', irrf_sobre_13: 'IRRF sobre 13º proporcional',
    saldo_salario: 'Saldo de salário', observacao: 'Observação',
  };
  return (
    <div className="space-y-0.5 text-slate-600">
      {Object.entries(memoria).map(([k, v]) => {
        if (v === null || v === undefined) return null;
        if (typeof v === 'object') {
          return (
            <div key={k} className="mt-1">
              <p className="font-semibold text-slate-700">{LABELS[k] || k.replaceAll('_', ' ')}:</p>
              <div className="pl-2 border-l-2 border-slate-200"><MemoriaLegivel memoria={v} /></div>
            </div>
          );
        }
        const isMoney = typeof v === 'number' && !['avos', 'dias', 'dias_afastados', 'dias_pagos', 'dias_nao_pagos', 'percentual', 'aliquota', 'horas'].includes(k);
        const isPct = ['percentual', 'aliquota'].includes(k) && typeof v === 'number' && v <= 1;
        return (
          <p key={k}>
            {LABELS[k] || k.replaceAll('_', ' ')}: <b>{isMoney ? fmtBRL(v) : isPct ? fmtPct(v) : String(v)}</b>
          </p>
        );
      })}
    </div>
  );
};

const TIPOS_RUB = [
  { v: 'provento', l: 'Provento', color: 'bg-emerald-500' },
  { v: 'desconto', l: 'Desconto', color: 'bg-red-500' },
  { v: 'informativa', l: 'Informativa', color: 'bg-slate-400' },
];

const TIPOS_COMP = [
  { v: 'folha_mensal', l: 'Folha Mensal' },
  { v: 'adiantamento', l: 'Adiantamento' },
  { v: '13_primeira', l: '13º — 1ª parcela' },
  { v: '13_segunda', l: '13º — 2ª parcela' },
  { v: 'ferias', l: 'Férias' },
];

const AsyncEmployeeSelect = ({
  value,
  items,
  selectedItem,
  loading,
  open,
  onOpenChange,
  onSearchChange,
  onValueChange,
  placeholder = 'Selecione um colaborador...',
  emptyMessage = 'Nenhum colaborador encontrado.',
}) => {
  const selected = selectedItem || items.find((item) => item.id === value);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{selected ? `${selected.nome} — ${selected.cargo || 'Sem cargo'}` : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar por nome..." onValueChange={onSearchChange} />
          <CommandList>
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando colaboradores...
              </div>
            ) : (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            )}
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.id}
                  value={`${item.nome} ${item.cargo || ''}`}
                  onSelect={() => {
                    onValueChange(item.id === value ? '' : item.id);
                    onOpenChange(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === item.id ? 'opacity-100' : 'opacity-0')} />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate">{item.nome}</span>
                    {item.cargo && <span className="text-xs text-slate-500">{item.cargo}</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// ============================================================================
// SUB-TAB: RUBRICAS
// ============================================================================
const RubricasTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos');
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = {
    codigo: '', descricao: '', tipo: 'provento', natureza: 'fixa', formula_tipo: 'valor_fixo',
    codigo_esocial: '', incide_inss: false, incide_irrf: false, incide_fgts: false,
    incide_pensao: false,
    incide_dsr: false, incide_13: false, incide_ferias: false, auto_lancar_mensal: false, ativo: true,
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/rubricas`, authHeader());
      setItems(r.data || []);
    } catch (e) { toast.error('Erro ao carregar rubricas'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(it => filtro === 'todos' || it.tipo === filtro);

  const save = async () => {
    if (!form.codigo || !form.descricao) return toast.error('Código e descrição obrigatórios');
    try {
      if (edit) await axios.put(`${API}/api/calculos-dp/rubricas/${edit}`, form, authHeader());
      else await axios.post(`${API}/api/calculos-dp/rubricas`, form, authHeader());
      toast.success(edit ? 'Atualizada' : 'Criada');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Excluir esta rubrica?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/rubricas/${id}`, authHeader());
      toast.success('Excluída'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-2 items-center text-sm">
          <span className="text-slate-600">{items.length} rubrica(s) ·</span>
          {['todos', 'provento', 'desconto', 'informativa'].map((f) => (
            <Button key={f} size="sm" variant={filtro === f ? 'default' : 'ghost'} onClick={() => setFiltro(f)}>{f === 'todos' ? 'Todas' : f.charAt(0).toUpperCase() + f.slice(1)}</Button>
          ))}
        </div>
        <Button onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} data-testid="dp-rub-novo-btn">
          <Plus className="h-4 w-4 mr-2" /> Nova Rubrica
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Código</th>
              <th className="text-left p-3">Descrição</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">eSocial</th>
              <th className="text-center p-3">Incidências</th>
              <th className="text-center p-3">Auto</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="p-4 text-center text-slate-500">Nenhuma rubrica.</td></tr>
            ) : filtered.map((r) => {
              const t = TIPOS_RUB.find(x => x.v === r.tipo);
              return (
                <tr key={r.id} className="border-t hover:bg-slate-50">
                  <td className="p-3 font-mono">{r.codigo}</td>
                  <td className="p-3">{r.descricao}</td>
                  <td className="p-3"><Badge className={`${t?.color} text-white`}>{t?.l}</Badge></td>
                  <td className="p-3 font-mono text-xs">{r.codigo_esocial || '—'}</td>
                  <td className="p-3 text-center text-xs">
                    {r.incide_inss && <Badge variant="outline" className="text-[10px] mr-1">INSS</Badge>}
                    {r.incide_irrf && <Badge variant="outline" className="text-[10px] mr-1">IRRF</Badge>}
                    {r.incide_fgts && <Badge variant="outline" className="text-[10px] mr-1">FGTS</Badge>}
                    {r.incide_pensao && <Badge variant="outline" className="text-[10px] mr-1">Pensão</Badge>}
                    {r.incide_dsr && <Badge variant="outline" className="text-[10px] mr-1">DSR</Badge>}
                  </td>
                  <td className="p-3 text-center">{r.auto_lancar_mensal ? <CheckCircle2 className="h-4 w-4 inline text-emerald-600" /> : '—'}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setEdit(r.id); setForm({ ...empty, ...r, codigo_esocial: r.codigo_esocial || '' }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dp-rub-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? 'Editar Rubrica' : 'Nova Rubrica'}</DialogTitle>
            <DialogDescription>Defina código, incidências e código eSocial (Tabela 4).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Código *</Label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></div>
            <div>
              <Label>Tipo *</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_RUB.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Descrição *</Label><Input value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
            <div><Label>Natureza</Label>
              <Select value={form.natureza} onValueChange={(v) => setForm({ ...form, natureza: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="fixa">Fixa</SelectItem><SelectItem value="variavel">Variável</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Código eSocial</Label><Input value={form.codigo_esocial} onChange={(e) => setForm({ ...form, codigo_esocial: e.target.value })} placeholder="ex.: 1000, 9201" /></div>
            <div className="col-span-2 mt-2">
              <Label className="text-base mb-2 block">Incidências</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { k: 'incide_inss', l: 'INSS' }, { k: 'incide_irrf', l: 'IRRF' }, { k: 'incide_fgts', l: 'FGTS' }, { k: 'incide_pensao', l: 'Pensão' },
                  { k: 'incide_dsr', l: 'DSR' }, { k: 'incide_13', l: '13º Salário' }, { k: 'incide_ferias', l: 'Férias' },
                ].map((c) => (
                  <div key={c.k} className="flex items-center gap-2">
                    <Switch checked={form[c.k]} onCheckedChange={(v) => setForm({ ...form, [c.k]: v })} />
                    <Label className="text-sm">{c.l}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-2 flex items-center gap-2 mt-2">
              <Switch checked={form.auto_lancar_mensal} onCheckedChange={(v) => setForm({ ...form, auto_lancar_mensal: v })} />
              <Label>Auto-lançar em folha mensal</Label>
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} />
              <Label>Ativa</Label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} data-testid="dp-rub-salvar-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUB-TAB: COMPETÊNCIAS
// ============================================================================
const CompetenciasTab = ({ onOpenHolerites }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [running, setRunning] = useState(null);
  const [calcDialog, setCalcDialog] = useState(null); // competência selecionada para cálculo
  const [calcModo, setCalcModo] = useState('coletivo');
  const [calcEmployee, setCalcEmployee] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployeeOption, setSelectedEmployeeOption] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const empty = { mes: new Date().getMonth() + 1, ano: new Date().getFullYear(), tipo: 'folha_mensal', empresa_id: '*', data_pagamento: '', observacoes: '', dsr_dias_repouso: '', dsr_dias_uteis: '' };
  const [form, setForm] = useState(empty);
  const [empresas, setEmpresas] = useState([]);
  const [filterEmpresa, setFilterEmpresa] = useState('*');
  const employeeRequestSeq = useRef(0);

  const getEmpresaName = useCallback((empresaId) => {
    if (!empresaId) return 'Todas';
    const e = empresas.find(emp => emp.id === empresaId);
    return e?.name || e?.razao_social || empresaId;
  }, [empresas]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterEmpresa !== '*' ? { empresa_id: filterEmpresa } : {};
      const [r, reEmp] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/competencias`, { ...authHeader(), params }),
        axios.get(`${API}/api/companies`, authHeader()),
      ]);
      setItems(r.data || []);
      setEmpresas(Array.isArray(reEmp.data) ? reEmp.data : []);
    } catch (e) { toast.error('Erro ao listar competências'); }
    finally { setLoading(false); }
  }, [filterEmpresa]);
  useEffect(() => { load(); }, [load]);

  const loadEmployeeOptions = useCallback(async (searchTerm = '') => {
    if (!calcDialog || calcModo !== 'individual') return;

    const requestSeq = ++employeeRequestSeq.current;
    setEmployeeLoading(true);

    try {
      const params = {
        regime: 'CLT,JOVEM_APRENDIZ,ESTAGIARIO',
        situacao: 'ativo',
        page: 1,
        limit: 20,
      };
      const normalizedSearch = searchTerm.trim();
      if (normalizedSearch) params.search = normalizedSearch;
      if (calcDialog.empresa_id) params.company_id = calcDialog.empresa_id;

      const r = await axios.get(`${API}/api/employees-paged`, { ...authHeader(), params });
      const nextItems = Array.isArray(r.data?.data) ? r.data.data : [];
      if (requestSeq === employeeRequestSeq.current) {
        setEmployeeOptions(nextItems);
      }
    } catch (e) {
      toast.error('Erro ao buscar colaboradores');
    } finally {
      if (requestSeq === employeeRequestSeq.current) {
        setEmployeeLoading(false);
      }
    }
  }, [calcDialog, calcModo]);

  useEffect(() => {
    if (!calcDialog || calcModo !== 'individual') return undefined;
    const timer = window.setTimeout(() => {
      loadEmployeeOptions(employeeSearch);
    }, employeeSearch.trim() ? 300 : 0);

    return () => window.clearTimeout(timer);
  }, [calcDialog, calcModo, employeeSearch, loadEmployeeOptions]);

  const create = async () => {
    try {
      const payload = {
        ...form,
        empresa_id: form.empresa_id === '*' ? null : form.empresa_id,
        dsr_dias_repouso: form.dsr_dias_repouso === '' ? null : Number(form.dsr_dias_repouso),
        dsr_dias_uteis: form.dsr_dias_uteis === '' ? null : Number(form.dsr_dias_uteis),
      };
      await axios.post(`${API}/api/calculos-dp/competencias`, payload, authHeader());
      toast.success('Competência criada'); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  const update = async () => {
    try {
      const payload = {
        ...form,
        empresa_id: form.empresa_id === '*' ? null : form.empresa_id,
        dsr_dias_repouso: form.dsr_dias_repouso === '' ? null : Number(form.dsr_dias_repouso),
        dsr_dias_uteis: form.dsr_dias_uteis === '' ? null : Number(form.dsr_dias_uteis),
      };
      await axios.put(`${API}/api/calculos-dp/competencias/${editId}`, payload, authHeader());
      toast.success('Competência atualizada'); setOpen(false); setEditId(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  const salvar = editId ? update : create;
  const editar = (c) => {
    setForm({ mes: c.mes, ano: c.ano, tipo: c.tipo, empresa_id: c.empresa_id || '*', descricao: c.descricao || '', data_pagamento: c.data_pagamento || '', observacoes: c.observacoes || '', dsr_dias_repouso: c.dsr_dias_repouso ?? '', dsr_dias_uteis: c.dsr_dias_uteis ?? '' });
    setEditId(c.id);
    setOpen(true);
  };
  const remove = async (id) => {
    if (!window.confirm('Excluir competência (e todos os holerites associados)?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/competencias/${id}`, authHeader());
      toast.success('Excluída'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  const resetCalcState = () => {
    employeeRequestSeq.current += 1;
    setCalcDialog(null);
    setCalcModo('coletivo');
    setCalcEmployee('');
    setSelectedEmployeeOption(null);
    setEmployeeOptions([]);
    setEmployeeSearch('');
    setEmployeeLoading(false);
    setEmployeeOpen(false);
  };
  const abrirCalcDialog = (comp) => {
    setCalcDialog(comp);
    setCalcModo('coletivo');
    setCalcEmployee('');
    setSelectedEmployeeOption(null);
    setEmployeeOptions([]);
    setEmployeeSearch('');
    setEmployeeOpen(false);
  };

  const calcular = async () => {
    if (!calcDialog) return;
    if (calcModo === 'individual' && !calcEmployee) return toast.error('Selecione o colaborador');
    const id = calcDialog.id;
    setRunning(id);
    try {
      const qs = calcModo === 'individual' ? `?employee_id=${calcEmployee}` : '';
      const r = await axios.post(`${API}/api/calculos-dp/competencias/${id}/calcular${qs}`, {}, authHeader());
      toast.success(`✅ ${r.data.holerites_processados} holerite(s) calculado(s) (líquido total ${fmtBRL(r.data.totais_agregados?.liquido)})`, { duration: 5000 });
      resetCalcState();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setRunning(null); }
  };
  const fechar = async (id) => {
    try {
      await axios.post(`${API}/api/calculos-dp/competencias/${id}/fechar`, {}, authHeader());
      toast.success('Fechada'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  const reabrir = async (id) => {
    try {
      await axios.post(`${API}/api/calculos-dp/competencias/${id}/reabrir`, {}, authHeader());
      toast.success('Reaberta'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  const gerarLotePDF = async (id) => {
    if (!window.confirm('Gerar PDF de TODOS os holerites desta competência? Isso pode levar alguns segundos.')) return;
    setRunning(id);
    try {
      const r = await axios.post(`${API}/api/calculos-dp/competencias/${id}/holerites/gerar-pdf-lote`, {},
        { ...authHeader(), responseType: 'blob' });
      const gerados = parseInt(r.headers['x-gerados'] || '0');
      const total = parseInt(r.headers['x-total'] || '0');
      const erros = parseInt(r.headers['x-erros'] || '0');
      toast.success(`✅ ${gerados}/${total} PDFs gerados${erros ? ` · ${erros} erro(s)` : ''}`, { duration: 5000 });
      if (gerados > 0) {
        const url = URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
        const match = r.headers['content-disposition']?.match(/filename="?(.+?)"?$/);
        const filename = match?.[1] || 'holerites.zip';
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      let msg = 'Erro';
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const json = JSON.parse(text);
          msg = json.detail || text;
        } catch { msg = 'Erro ao gerar PDFs'; }
      } else {
        msg = e?.response?.data?.detail || 'Erro';
      }
      toast.error(msg);
    }
    finally { setRunning(null); }
  };
  const enviarHolerites = async (competencia) => {
    if (competencia.status !== 'fechada') return toast.error('Feche a competência antes de enviar os holerites');
    if (!window.confirm('Enviar a via original por e-mail e o link de assinatura por WhatsApp para os colaboradores desta competência?')) return;
    setRunning(competencia.id);
    try {
      const r = await axios.post(`${API}/api/calculos-dp/competencias/${competencia.id}/holerites/enviar`, {}, authHeader());
      toast.success(`${r.data.enviados} holerite(s) enviado(s) · E-mail: ${r.data.email_enviados || 0} · WhatsApp: ${r.data.whatsapp_enviados || 0} · ${r.data.pendentes} pendente(s) · ${r.data.erros} erro(s)`, { duration: 7000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao enviar holerites'); }
    finally { setRunning(null); }
  };

  const statusBadge = (s) => {
    if (s === 'aberta') return <Badge variant="outline">Aberta</Badge>;
    if (s === 'calculada') return <Badge className="bg-indigo-500">Calculada</Badge>;
    if (s === 'fechada') return <Badge className="bg-emerald-600">Fechada</Badge>;
    return <Badge className="bg-slate-700">{s}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <p className="text-sm text-slate-600">{items.length} competência(s)</p>
          <Select value={filterEmpresa} onValueChange={(v) => setFilterEmpresa(v)}>
            <SelectTrigger className="w-52 h-8 text-xs"><SelectValue placeholder="Filtrar por empresa" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="*">Todas as empresas</SelectItem>
              {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.name || e.razao_social || e.id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="dp-comp-novo-btn">
          <Plus className="h-4 w-4 mr-2" /> Nova Competência
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Período</th>
              <th className="text-left p-3">Empresa</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Holerites</th>
              <th className="text-right p-3">Líquido Total</th>
              <th className="text-left p-3">Pagamento</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr>
              : items.length === 0 ? <tr><td colSpan={8} className="p-4 text-center text-slate-500">Nenhuma competência.</td></tr>
                : items.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-medium">{MESES[c.mes - 1]}/{c.ano}</td>
                    <td className="p-3 text-xs">{getEmpresaName(c.empresa_id)}</td>
                    <td className="p-3 text-xs">{TIPOS_COMP.find(t => t.v === c.tipo)?.l}</td>
                    <td className="p-3">{statusBadge(c.status)}</td>
                    <td className="p-3 text-right">{c.totais?.holerites_processados || 0}</td>
                    <td className="p-3 text-right font-semibold">{fmtBRL(c.totais?.liquido)}</td>
                    <td className="p-3 text-xs">{formatDateBR(c.data_pagamento)}</td>
                    <td className="p-3 text-right">
                      {['aberta', 'calculada'].includes(c.status) && (
                        <Button size="sm" variant="ghost" onClick={() => editar(c)} title="Editar"><Pencil className="h-4 w-4 text-slate-600" /></Button>
                      )}
                      {['aberta', 'calculada'].includes(c.status) && (
                        <Button size="sm" variant="ghost" onClick={() => abrirCalcDialog(c)} disabled={running === c.id} title="Calcular folha">
                          {running === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 text-emerald-600" />}
                        </Button>
                      )}
                      {c.status === 'calculada' && (
                        <Button size="sm" variant="ghost" onClick={() => fechar(c.id)} title="Fechar"><Lock className="h-4 w-4 text-indigo-600" /></Button>
                      )}
                      {c.status === 'fechada' && (
                        <Button size="sm" variant="ghost" onClick={() => reabrir(c.id)} title="Reabrir"><Unlock className="h-4 w-4 text-amber-600" /></Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onOpenHolerites(c)} title="Ver holerites"><Eye className="h-4 w-4" /></Button>
                      {(c.status === 'calculada' || c.status === 'fechada') && (
                        <Button size="sm" variant="ghost" onClick={() => gerarLotePDF(c.id)} disabled={running === c.id} title="Gerar PDFs em lote"
                          data-testid={`dp-comp-pdf-lote-${c.id}`}>
                          {running === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus className="h-4 w-4 text-amber-600" />}
                        </Button>
                      )}
                      {c.status === 'fechada' && (
                        <Button size="sm" variant="ghost" onClick={() => enviarHolerites(c)} disabled={running === c.id} title="Enviar holerites por e-mail" data-testid={`dp-comp-enviar-holerites-${c.id}`}>
                          {running === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-emerald-600" />}
                        </Button>
                      )}
                      {['aberta', 'calculada'].includes(c.status) && (
                        <Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      )}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setEditId(null); setForm(empty); } setOpen(o); }}>
        <DialogContent data-testid="dp-comp-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Editar Competência' : 'Nova Competência'}</DialogTitle>
            <DialogDescription>{editId ? 'Altere os dados da competência.' : 'Crie o período de folha. Após criar, clique no botão Play (▶) para calcular.'}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Mês *</Label>
              <Select value={String(form.mes)} onValueChange={(v) => setForm({ ...form, mes: parseInt(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Ano *</Label><Input value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value.replace(/\D/g, '').slice(0, 4) })} inputMode="numeric" /></div>
            <div className="col-span-2">
              <Label>Tipo *</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_COMP.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Empresa</Label>
              <Select value={form.empresa_id || '*'} onValueChange={(v) => setForm({ ...form, empresa_id: v })}>
                <SelectTrigger><SelectValue placeholder="Todas as empresas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="*">Todas as empresas</SelectItem>
                  {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.name || e.razao_social || e.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Data de Pagamento</Label><Input type="date" value={form.data_pagamento} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })} /></div>
            <div>
              <Label>Dias de repouso DSR</Label>
              <Input type="number" min="0" step="1" placeholder="Automático" value={form.dsr_dias_repouso}
                onChange={(e) => setForm({ ...form, dsr_dias_repouso: e.target.value })} />
            </div>
            <div>
              <Label>Dias úteis para DSR</Label>
              <Input type="number" min="1" step="1" placeholder="Automático" value={form.dsr_dias_uteis}
                onChange={(e) => setForm({ ...form, dsr_dias_uteis: e.target.value })} />
            </div>
            <div className="col-span-2 text-xs text-slate-500">Deixe em branco para calcular domingos, feriados e dias úteis automaticamente a partir do calendário cadastrado.</div>
            <div className="col-span-2"><Label>Observações</Label><Input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditId(null); setForm(empty); }}>Cancelar</Button>
            <Button onClick={salvar} data-testid="dp-comp-salvar-btn">{editId ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!calcDialog} onOpenChange={(o) => !o && resetCalcState()}>
        <DialogContent data-testid="dp-comp-calc-dialog">
          <DialogHeader>
            <DialogTitle>Calcular {calcDialog ? `${MESES[calcDialog.mes - 1]}/${calcDialog.ano}` : ''}</DialogTitle>
            <DialogDescription>Escolha calcular para todos os colaboradores ativos (coletivo) ou apenas um (individual).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label>Modo de cálculo *</Label>
              <Select value={calcModo} onValueChange={setCalcModo}>
                <SelectTrigger data-testid="dp-comp-calc-modo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="coletivo">Coletivo (todos os colaboradores ativos)</SelectItem>
                  <SelectItem value="individual">Individual (um colaborador)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {calcModo === 'individual' && (
              <div>
                <Label>Colaborador *</Label>
                <AsyncEmployeeSelect
                  value={calcEmployee}
                  items={employeeOptions}
                  selectedItem={selectedEmployeeOption}
                  loading={employeeLoading}
                  open={employeeOpen}
                  onOpenChange={setEmployeeOpen}
                  onSearchChange={setEmployeeSearch}
                  onValueChange={(employeeId) => {
                    setCalcEmployee(employeeId);
                    setSelectedEmployeeOption(
                      employeeId ? employeeOptions.find((item) => item.id === employeeId) || null : null
                    );
                  }}
                  emptyMessage={employeeSearch.trim()
                    ? 'Nenhum colaborador encontrado para essa busca.'
                    : 'Nenhum colaborador ativo encontrado para essa competência.'}
                />
                <p className="mt-2 text-xs text-slate-500">
                  A lista é carregada sob demanda e filtrada para colaboradores CLT, jovens aprendizes e estagiários ativos
                  {calcDialog?.empresa_id ? ' da empresa desta competência' : ''}.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetCalcState}>Cancelar</Button>
            <Button onClick={calcular} disabled={running === calcDialog?.id} data-testid="dp-comp-calc-confirm">
              {running === calcDialog?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Calcular'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUB-TAB: HOLERITES (de uma competência selecionada)
// ============================================================================
const HoleritesTab = ({ competencia, onBack }) => {
  const competenciaAberta = ['aberta', 'calculada'].includes(competencia?.status);
  const [data, setData] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [memoriaExpandida, setMemoriaExpandida] = useState(null);
  const [pontoPreview, setPontoPreview] = useState(null); // { holerite_id, itens, resumo }
  const [pontoLoading, setPontoLoading] = useState(false);
  const [rubricas, setRubricas] = useState([]);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const manualEmpty = { rubrica_id: '', valor: '', referencia: '', horas: '' };
  const [manualForm, setManualForm] = useState(manualEmpty);
  const [manualSaving, setManualSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // item em edição; null = adição nova
  const [criandoRubrica, setCriandoRubrica] = useState(false);
  const [novaRubrica, setNovaRubrica] = useState({ codigo: '', descricao: '', tipo: 'provento', incide_inss: false, incide_irrf: false, incide_fgts: false, incide_pensao: false });
  const [afastModalOpen, setAfastModalOpen] = useState(false);
  const [recalculando, setRecalculando] = useState(null);
  const [horaExtraDialogOpen, setHoraExtraDialogOpen] = useState(false);
  const [horaExtraSaving, setHoraExtraSaving] = useState(false);
  const [horaExtraForm, setHoraExtraForm] = useState({ rubrica_id: '', modo: 'horas', horas: '', valor: '', referencia: '', observacao: '' });
  const [editingHoraExtra, setEditingHoraExtra] = useState(null);

  const load = useCallback(async () => {
    if (!competencia) return;
    try {
      const r = await axios.get(`${API}/api/calculos-dp/competencias/${competencia.id}/holerites`, authHeader());
      setData(r.data);
    } catch (e) { toast.error('Erro ao carregar holerites'); }
  }, [competencia]);
  useEffect(() => { load(); }, [load]);

  const verDetalhe = async (id) => {
    try {
      const r = await axios.get(`${API}/api/calculos-dp/holerites/${id}`, authHeader());
      setDetalhe(r.data);
    } catch (e) { toast.error('Erro'); }
  };

  const previewPonto = async (id) => {
    setPontoLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/holerites/${id}/preview-ponto`, authHeader());
      setPontoPreview({ holerite_id: id, ...r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao prever importação do ponto');
    } finally { setPontoLoading(false); }
  };

  const confirmarImportPonto = async () => {
    if (!pontoPreview) return;
    setPontoLoading(true);
    try {
      const r = await axios.post(`${API}/api/calculos-dp/holerites/${pontoPreview.holerite_id}/importar-ponto`, {}, authHeader());
      toast.success(`${r.data.importados} rubrica(s) importada(s) do ponto`);
      setPontoPreview(null);
      if (r.data.holerite) setDetalhe((d) => d ? { ...d, holerite: r.data.holerite } : d);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao importar do ponto');
    } finally { setPontoLoading(false); }
  };

 const abrirManualDialog = async () => {
  setEditingItem(null);
  setManualForm(manualEmpty);
  setManualDialogOpen(true);
  try {
    const r = await axios.get(`${API}/api/calculos-dp/rubricas?ativo=true`, authHeader());
    setRubricas(r.data || []);
  } catch (e) { toast.error('Erro ao carregar rubricas'); }
};

  const abrirEditarManual = async (item) => {
  setEditingItem(item);
  setManualForm({
    rubrica_id: item.rubrica_id || '',
    valor: String(item.tipo === 'desconto' ? item.valor_desconto : item.valor_provento),
    referencia: item.referencia || '',
    horas: item.memoria?.horas_informadas || item.memoria?.horas || '',
  });
  setManualDialogOpen(true);
  try {
    const r = await axios.get(`${API}/api/calculos-dp/rubricas?ativo=true`, authHeader());
    setRubricas(r.data || []);
  } catch (e) { toast.error('Erro ao carregar rubricas'); }
};

  const salvarManual = async () => {
    const referencia = manualForm.referencia.trim();
    const valor = parseFloat(String(manualForm.valor).replace(',', '.'));
    if (!manualForm.rubrica_id) return toast.error('Selecione uma rubrica');
    const rubrica = rubricas.find(r => r.id === manualForm.rubrica_id);
    if (!rubrica) return toast.error('Rubrica inválida');
    if (rubrica.codigo === '301' ? !manualForm.horas : (!valor || valor <= 0)) return toast.error(rubrica.codigo === '301' ? 'Informe as horas negativas' : 'Informe um valor válido');
    if (!referencia) return toast.error('Informe a referência');
    setManualSaving(true);
    try {
       if (editingItem) {
      await axios.delete(
        `${API}/api/calculos-dp/holerites/${detalhe.holerite.id}/itens/${editingItem.id}`,
        authHeader()
      );
    }
      const r = await axios.post(`${API}/api/calculos-dp/holerites/${detalhe.holerite.id}/lancar-rubrica`, {
        rubrica_id: rubrica.id,
        valor_provento: rubrica.tipo === 'provento' ? valor : 0,
        valor_desconto: rubrica.tipo === 'desconto' ? valor : 0,
        referencia,
        horas: rubrica.codigo === '301' ? manualForm.horas : undefined,
      }, authHeader());
      setDetalhe((d) => {
        if (!d) return d;
        const semAntigo = editingItem
          ? d.holerite.itens.filter((it) => it.id !== editingItem.id)
          : d.holerite.itens;
        return {
          ...d,
          holerite: {
            ...d.holerite,
            itens: [...semAntigo, r.data],
            ...(r.data.totais || {}),
          },
        };
      });
      toast.success(editingItem ? 'Rubrica atualizada' : 'Rubrica adicionada ao holerite');
      setManualDialogOpen(false);
      setEditingItem(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (editingItem ? 'Erro ao editar desconto' : 'Erro ao adicionar desconto')); }
    finally { setManualSaving(false); }
  };

  const abrirHoraExtraDialog = async (item = null) => {
    const memoria = item?.memoria || {};
    setEditingHoraExtra(item);
    setHoraExtraForm({
      rubrica_id: item?.rubrica_id || '',
      modo: 'horas',
      horas: memoria.horas ? String(memoria.horas) : '',
      valor: '',
      referencia: item?.referencia || '',
      observacao: memoria.observacao || '',
    });
    try {
      const r = await axios.get(`${API}/api/calculos-dp/rubricas?ativo=true`, authHeader());
      setRubricas(r.data || []);
      setHoraExtraDialogOpen(true);
    } catch (e) { toast.error('Erro ao carregar rubricas de hora extra'); }
  };

  const salvarHoraExtra = async () => {
    const horas = horaExtraForm.horas === '' ? undefined : parseFloat(String(horaExtraForm.horas).replace(',', '.'));
    if (!horaExtraForm.rubrica_id) return toast.error('Selecione a rubrica de hora extra');
    if (!horas || horas <= 0) return toast.error('Informe uma quantidade de horas válida');
    setHoraExtraSaving(true);
    try {
      await axios.post(`${API}/api/calculos-dp/holerites/${detalhe.holerite.id}/lancar-hora-extra`, {
        rubrica_id: horaExtraForm.rubrica_id,
        modo: 'horas',
        horas,
        referencia: horaExtraForm.referencia.trim() || undefined,
        observacao: horaExtraForm.observacao.trim() || undefined,
        item_id: editingHoraExtra?.id || undefined,
      }, authHeader());
      toast.success(editingHoraExtra ? 'Hora extra atualizada' : 'Hora extra lançada');
      setHoraExtraDialogOpen(false);
      setEditingHoraExtra(null);
      await verDetalhe(detalhe.holerite.id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao lançar hora extra'); }
    finally { setHoraExtraSaving(false); }
  };

  const removerItemManual = async (item) => {
    if (!window.confirm(`Remover "${item.rubrica_descricao}" deste holerite?`)) return;
    try {
      const r = await axios.delete(`${API}/api/calculos-dp/holerites/${detalhe.holerite.id}/itens/${item.id}`, authHeader());
      setDetalhe((d) => d ? {
        ...d,
        holerite: {
          ...d.holerite,
          itens: d.holerite.itens.filter((it) => it.id !== item.id),
          ...(r.data.totais || {}),
        },
      } : d);
      toast.success('Item removido');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao remover item'); }
  };

  const recalcularHolerite = async (id) => {
    setRecalculando(id);
    try {
      const r = await axios.post(`${API}/api/calculos-dp/holerites/${id}/recalcular`, {}, authHeader());
      setDetalhe((d) => d && d.holerite?.id === id ? { ...d, holerite: r.data } : d);
      toast.success('Holerite recalculado');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao recalcular'); }
    finally { setRecalculando(null); }
  };

  // Uma rubrica só pode aparecer uma vez no mesmo demonstrativo. Em edição,
  // mantém a própria rubrica disponível para que o valor/referência seja alterado.
  const rubricasDisponiveis = rubricas.filter((rubrica) => !isHoraExtraFormula(rubrica.formula_tipo) && rubrica.formula_tipo !== 'dsr'
    && !detalhe?.holerite?.itens?.some((item) =>
    item.rubrica_id === rubrica.id && item.id !== editingItem?.id
  ));

  const gerarPDF = async (id, abrir = true) => {
    try {
      const r = await axios.post(`${API}/api/calculos-dp/holerites/${id}/gerar-pdf`, {},
        { ...authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (abrir) window.open(url, '_blank');
      toast.success('PDF gerado');
      return url;
    } catch (e) { toast.error('Erro ao gerar PDF'); }
  };

  const verPDF = async (id) => {
    try {
      const r = await axios.post(`${API}/api/calculos-dp/holerites/${id}/gerar-pdf`, {},
        { ...authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) { toast.error('Erro ao gerar PDF'); }
  };

  if (!competencia) {
    return <Card><CardContent className="p-6 text-center text-slate-500">Selecione uma competência na aba "Competências" para ver os holerites.</CardContent></Card>;
  }
  if (!data) return <div className="p-6 text-center text-slate-500"><Loader2 className="h-5 w-5 inline animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{MESES[competencia.mes - 1]}/{competencia.ano} — {TIPOS_COMP.find(t => t.v === competencia.tipo)?.l}</h3>
          <p className="text-xs text-slate-500">Status: {competencia.status}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onBack}>← Voltar para Competências</Button>
      </div>

      {/* Totais */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <Card><CardContent className="p-3"><div className="text-xs text-emerald-700 uppercase">Proventos</div><div className="text-xl font-bold text-emerald-700">{fmtBRL(data.competencia.totais?.proventos)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-red-700 uppercase">Descontos</div><div className="text-xl font-bold text-red-700">{fmtBRL(data.competencia.totais?.descontos)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-indigo-700 uppercase">Líquido</div><div className="text-xl font-bold text-indigo-700">{fmtBRL(data.competencia.totais?.liquido)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-amber-700 uppercase">FGTS a Recolher</div><div className="text-xl font-bold text-amber-700">{fmtBRL(data.competencia.totais?.fgts)}</div></CardContent></Card>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Colaborador</th>
              <th className="text-right p-3">Salário Base</th>
              <th className="text-right p-3">Proventos</th>
              <th className="text-right p-3">Descontos</th>
              <th className="text-right p-3">INSS</th>
              <th className="text-right p-3">IRRF</th>
              <th className="text-right p-3">Líquido</th>
              <th className="text-left p-3">Envio</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.holerites.length === 0 ? (
              <tr><td colSpan={9} className="p-4 text-center text-slate-500">Sem holerites. Calcule a competência primeiro.</td></tr>
            ) : data.holerites.map((h) => (
              <tr key={h.id} className="border-t hover:bg-slate-50">
                <td className="p-3"><div className="font-medium">{h.employee_nome}</div><div className="text-xs text-slate-500">{h.employee_cargo}</div></td>
                <td className="p-3 text-right text-xs">{fmtBRL(h.salario_base)}</td>
                <td className="p-3 text-right text-emerald-700">{fmtBRL(h.total_proventos)}</td>
                <td className="p-3 text-right text-red-700">{fmtBRL(h.total_descontos)}</td>
                <td className="p-3 text-right text-xs">{fmtBRL(h.desconto_inss)}</td>
                <td className="p-3 text-right text-xs">{fmtBRL(h.desconto_irrf)}</td>
                <td className="p-3 text-right font-bold text-indigo-700">{fmtBRL(h.total_liquido)}</td>
                <td className="p-3 text-xs"><Badge variant={h.status_envio === 'assinado' ? 'default' : h.status_envio === 'erro' ? 'destructive' : 'outline'}>{h.status_envio || 'pendente'}</Badge></td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => verDetalhe(h.id)} title="Ver detalhamento"><Receipt className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => verPDF(h.id)} title="Abrir holerite em PDF"
                    data-testid={`dp-holerite-pdf-${h.id}`}><FileDown className="h-4 w-4 text-indigo-600" /></Button>
                  {competenciaAberta && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => recalcularHolerite(h.id)}
                        disabled={recalculando === h.id} title="Recalcular holerite">
                        {recalculando === h.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 text-amber-600" />}
                      </Button>

                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog: detalhe do holerite */}
      <Dialog open={!!detalhe} onOpenChange={(o) => { if (!o) { setDetalhe(null); setMemoriaExpandida(null); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dp-holerite-detalhe">
          <DialogHeader>
            <DialogTitle>Holerite — {detalhe?.employee?.nome}</DialogTitle>
            <DialogDescription>Detalhamento completo das rubricas + memória de cálculo (INSS por faixa, IRRF tradicional vs simplificado).</DialogDescription>
          </DialogHeader>
          {detalhe && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div><span className="text-slate-500">Cargo:</span> {detalhe.employee?.cargo}</div>
                <div><span className="text-slate-500">Admissão:</span> {detalhe.employee?.admissao || '—'}</div>
                <div><span className="text-slate-500">Dependentes IRRF:</span> {detalhe.holerite?.dependentes_irrf || 0}</div>
              </div>
              {Number(detalhe.holerite?.irrf_acumulado_pendente || 0) > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  IRRF abaixo de R$ 10,00: saldo acumulado para a próxima folha: <b>{fmtBRL(detalhe.holerite.irrf_acumulado_pendente)}</b>.
                </div>
              )}
              {!competenciaAberta && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  Esta competência está protegida. Para lançar, editar ou remover rubricas, reabra a competência e confirme se o movimento de folha está correto.
                </div>
              )}

              <div className="flex items-center justify-between rounded-md border border-indigo-100 bg-indigo-50 p-2">
                <div className="text-xs text-indigo-700">
                  <span className="font-semibold">Ponte Ponto → Folha:</span> importa horas extras (50%/100%),
                  adicional noturno, DSR e faltas/atrasos do espelho de ponto deste mês.
                </div>
                <Button size="sm" variant="outline" disabled={pontoLoading || !competenciaAberta}
                  onClick={() => previewPonto(detalhe.holerite.id)}
                  data-testid="dp-holerite-importar-ponto">
                  {pontoLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Clock className="h-4 w-4 mr-1" />}
                  Importar do Ponto
                </Button>
              </div>
              <div className="flex justify-end">
                <Button size="sm" variant="outline" className="mr-2" disabled={!competenciaAberta}
                  onClick={() => abrirHoraExtraDialog()} data-testid="dp-holerite-add-hora-extra">
                  <Clock className="h-4 w-4 mr-1" /> Lançar Hora Extra
                </Button>
                <Button size="sm" variant="outline" disabled={!competenciaAberta}
                  onClick={abrirManualDialog} data-testid="dp-holerite-add-rubrica">
                  <Plus className="h-4 w-4 mr-1" /> Lançar Rubrica Manual
                </Button>
              </div>
              <table className="w-full text-sm border rounded-md overflow-hidden">
                <thead className="bg-slate-100 text-xs uppercase">
                  <tr><th className="text-left p-2">Cód.</th><th className="text-left p-2">Rubrica</th><th className="text-left p-2">Ref.</th><th className="text-right p-2">Provento</th><th className="text-right p-2">Desconto</th><th className="text-right p-2"></th></tr>
                </thead>
                <tbody>
                  {detalhe.holerite.itens.map((it, i) => (
                    <React.Fragment key={i}>
                      <tr className={`border-t ${it.tipo === 'informativa' ? 'bg-slate-50 text-slate-500' : ''}`}>
                        <td className="p-2 font-mono text-xs">{it.rubrica_codigo}</td>
                        <td className="p-2">
                          {it.rubrica_descricao}
                          <span className="text-xs text-slate-400">({it.origem})</span>
                          {it.rubrica_codigo === '001' && detalhe.holerite.afastamento_info?.dias_nao_pagos > 0 && (
                            <Button size="sm" variant="ghost" className="ml-1 h-5 w-5 p-0" onClick={() => setAfastModalOpen(true)} title="Ver cálculo do salário base">
                              <span className="text-amber-600 text-xs font-bold">🔍</span>
                            </Button>
                          )}
                        </td>
                        <td className="p-2 text-xs">{it.referencia || '—'}</td>
                        <td className="p-2 text-right text-emerald-700">{it.valor_provento > 0 ? fmtBRL(it.valor_provento) : '—'}</td>
                        <td className="p-2 text-right text-red-700">{it.valor_desconto > 0 ? fmtBRL(it.valor_desconto) : '—'}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          {it.memoria && (
                            <Button size="sm" variant="ghost" onClick={() => setMemoriaExpandida(memoriaExpandida === i ? null : i)}
                              title="Ver memória de cálculo" className="h-7 text-[10px] text-indigo-600">
                              <Eye className="h-3.5 w-3.5 mr-1" /> Memória
                            </Button>
                          )}
                          {it.origem === 'manual' && competenciaAberta && (
                            <>
                              <Button size="sm" variant="ghost"
                                onClick={() => ['100', '101', '102'].includes(it.rubrica_codigo) ? abrirHoraExtraDialog(it) : abrirEditarManual(it)} title="Editar item">
                                <Pencil className="h-4 w-4 text-slate-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => removerItemManual(it)} title="Remover item">
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          )}
                          {it.origem !== 'manual' && it.id && competenciaAberta && (
                            <Button size="sm" variant="ghost" onClick={() => removerItemManual(it)} title="Excluir lançamento automático">
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </td>
                      </tr>
                      {memoriaExpandida === i && it.memoria && (
                        <tr key={`mem-${i}`} className="bg-slate-50/50">
                          <td colSpan={6} className="p-3 pl-8 border-t border-slate-200">
                            <MemoriaLegivel memoria={it.memoria} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold">
                  <tr><td colSpan={3} className="p-2 text-right">Subtotais →</td>
                    <td className="p-2 text-right text-emerald-700">{fmtBRL(detalhe.holerite.total_proventos)}</td>
                    <td className="p-2 text-right text-red-700">{fmtBRL(detalhe.holerite.total_descontos)}</td>
                    <td></td>
                  </tr>
                  <tr><td colSpan={3} className="p-2 text-right text-base">LÍQUIDO A RECEBER →</td>
                    <td colSpan={3} className="p-2 text-right text-xl font-bold text-indigo-700">{fmtBRL(detalhe.holerite.total_liquido)}</td>
                  </tr>
                </tfoot>
              </table>

              {/* Memória de cálculo */}
              <details className="text-xs">
                <summary className="cursor-pointer font-semibold text-slate-700">📊 Memória de Cálculo (auditoria)</summary>
                <div className="mt-2 space-y-2">
                  {detalhe.holerite.itens.filter(it => it.memoria).map((it, i) => (
                    <Card key={i} className="bg-slate-50">
                      <CardHeader className="pb-1"><CardTitle className="text-xs">{it.rubrica_codigo} — {it.rubrica_descricao}</CardTitle></CardHeader>
                      <CardContent>
                        <MemoriaLegivel memoria={it.memoria} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: lançamento manual sempre vinculado ao catálogo de rubricas */}
      <Dialog open={manualDialogOpen} onOpenChange={(o) => { if (!o) { setManualForm(manualEmpty); setEditingItem(null); } setManualDialogOpen(o); }}>
         <DialogContent data-testid="dp-holerite-add-desconto-dialog">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Rubrica Manual' : 'Lançar Rubrica Manual'}</DialogTitle>
            <DialogDescription>
              A rubrica determina se o valor é provento ou desconto e quais bases serão recalculadas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Rubrica *</Label>
              <Select value={manualForm.rubrica_id} onValueChange={(v) => setManualForm({ ...manualForm, rubrica_id: v })}>
                <SelectTrigger data-testid="dp-manual-rubrica"><SelectValue placeholder="Selecione a rubrica" /></SelectTrigger>
                <SelectContent>{rubricasDisponiveis.length > 0
                  ? rubricasDisponiveis.map(r => <SelectItem key={r.id} value={r.id}>{r.codigo} — {r.descricao} ({r.tipo})</SelectItem>)
                  : <SelectItem value="__sem-rubricas" disabled>Nenhuma rubrica disponível</SelectItem>}
                </SelectContent>
              </Select>
              <Button type="button" variant="link" className="px-0 h-7" onClick={() => setCriandoRubrica(!criandoRubrica)}>+ Criar rubrica</Button>
              {criandoRubrica && <div className="grid grid-cols-2 gap-2 rounded border p-2">
                <Input placeholder="Código *" value={novaRubrica.codigo} onChange={e => setNovaRubrica({ ...novaRubrica, codigo: e.target.value })} />
                <Select value={novaRubrica.tipo} onValueChange={v => setNovaRubrica({ ...novaRubrica, tipo: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="provento">Provento</SelectItem><SelectItem value="desconto">Desconto</SelectItem></SelectContent></Select>
                <Input className="col-span-2" placeholder="Descrição *" value={novaRubrica.descricao} onChange={e => setNovaRubrica({ ...novaRubrica, descricao: e.target.value })} />
                {['inss', 'irrf', 'fgts', 'pensao'].map(k => <div key={k} className="flex items-center gap-1"><Switch checked={novaRubrica[`incide_${k}`]} onCheckedChange={v => setNovaRubrica({ ...novaRubrica, [`incide_${k}`]: v })} /><Label className="text-xs">{k.toUpperCase()}</Label></div>)}
                <Button type="button" size="sm" className="col-span-2" onClick={async () => { if (!novaRubrica.codigo || !novaRubrica.descricao) return toast.error('Código e descrição são obrigatórios'); try { const rc = await axios.post(`${API}/api/calculos-dp/rubricas`, { ...novaRubrica, natureza: 'variavel', formula_tipo: 'valor_fixo' }, authHeader()); setRubricas([...rubricas, rc.data]); setManualForm({ ...manualForm, rubrica_id: rc.data.id }); setCriandoRubrica(false); toast.success('Rubrica criada'); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao criar rubrica'); } }}>Salvar rubrica</Button>
              </div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {rubricas.find((r) => r.id === manualForm.rubrica_id)?.codigo === '301' && (
                <div>
                  <Label>Horas negativas do banco *</Label>
                  <Input value={manualForm.horas} onChange={(e) => setManualForm({ ...manualForm, horas: e.target.value })}
                    placeholder="Ex.: 1,52" data-testid="dp-manual-banco-horas" />
                  <p className="mt-1 text-xs text-slate-500">O sistema calcula o desconto pela carga horária e recalcula INSS, IRRF e FGTS sobre a base reduzida.</p>
                </div>
              )}
              <div>
                <Label>{rubricas.find((r) => r.id === manualForm.rubrica_id)?.codigo === '301' ? 'Valor calculado (R$)' : 'Valor (R$) *'}</Label>
                <Input type="number" step="0.01" min="0" value={manualForm.valor}
                  disabled={rubricas.find((r) => r.id === manualForm.rubrica_id)?.codigo === '301'}
                  onChange={(e) => setManualForm({ ...manualForm, valor: e.target.value })} data-testid="dp-manual-valor" />
              </div>
              <div>
                <Label>Referência *</Label>
                <Input value={manualForm.referencia} onChange={(e) => setManualForm({ ...manualForm, referencia: e.target.value })}
                  placeholder="Ex.: Julho/2026" data-testid="dp-manual-referencia" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialogOpen(false)} disabled={manualSaving}>Cancelar</Button>
            <Button onClick={salvarManual} disabled={manualSaving || !competenciaAberta} data-testid="dp-manual-salvar">
              {manualSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                : editingItem ? <Pencil className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              {editingItem ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: hora extra por quantidade ou valor */}
      <Dialog open={horaExtraDialogOpen} onOpenChange={(o) => { if (!o) setEditingHoraExtra(null); setHoraExtraDialogOpen(o); }}>
        <DialogContent data-testid="dp-holerite-hora-extra-dialog">
          <DialogHeader>
            <DialogTitle>{editingHoraExtra ? 'Editar Hora Extra' : 'Lançar Hora Extra'}</DialogTitle>
            <DialogDescription>
              Informe somente a quantidade de horas extras realizadas no mês. O sistema calculará o valor da hora extra e o DSR automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Rubrica *</Label>
              <Select value={horaExtraForm.rubrica_id} onValueChange={(v) => setHoraExtraForm({ ...horaExtraForm, rubrica_id: v })}>
                <SelectTrigger data-testid="dp-hora-extra-rubrica"><SelectValue placeholder="Selecione a rubrica de hora extra" /></SelectTrigger>
                <SelectContent>
                  {rubricas.filter((r) => isHoraExtraFormula(r.formula_tipo)).length === 0 ? (
                    <SelectItem value="__sem_rubrica_hora_extra__" disabled>Nenhuma rubrica de hora extra cadastrada</SelectItem>
                  ) : (
                    rubricas
                      .filter((r) => isHoraExtraFormula(r.formula_tipo))
                      .map((r) => {
                        const percentual = getHoraExtraPercentual(r.formula_tipo);
                        return (
                          <SelectItem key={r.id} value={r.id}>
                            {r.codigo} — {r.descricao}{percentual !== null ? ` (${String(percentual).replace('.', ',')}%)` : ''}
                          </SelectItem>
                        );
                      })
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantidade de horas *</Label>
                <Input type="number" min="0.01" step="0.01" value={horaExtraForm.horas}
                  onChange={(e) => setHoraExtraForm({ ...horaExtraForm, horas: e.target.value })}
                  placeholder="Ex.: 8,50" data-testid="dp-hora-extra-horas" />
              </div>
              <div>
                <Label>Valor calculado</Label>
                <Input value="Calculado pelo sistema após o lançamento" disabled data-testid="dp-hora-extra-valor" />
              </div>
            </div>
            <div>
              <Label>Referência</Label>
              <Input value={horaExtraForm.referencia} onChange={(e) => setHoraExtraForm({ ...horaExtraForm, referencia: e.target.value })}
                placeholder="Ex.: HE aprovada pelo gestor" />
            </div>
            <div>
              <Label>Observação</Label>
              <Input value={horaExtraForm.observacao} onChange={(e) => setHoraExtraForm({ ...horaExtraForm, observacao: e.target.value })} />
            </div>
            <div className="rounded border border-indigo-100 bg-indigo-50 p-2 text-xs text-indigo-700">
              O DSR será recalculado automaticamente com as horas extras do ponto e deste lançamento. Se já houver a mesma rubrica neste holerite, o sistema bloqueará um novo lançamento.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoraExtraDialogOpen(false)} disabled={horaExtraSaving}>Cancelar</Button>
            <Button onClick={salvarHoraExtra} disabled={horaExtraSaving || !competenciaAberta} data-testid="dp-hora-extra-salvar">
              {horaExtraSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Clock className="h-4 w-4 mr-1" />}
              {editingHoraExtra ? 'Atualizar' : 'Lançar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: confirmar importação do ponto */}
      <Dialog open={!!pontoPreview} onOpenChange={(o) => !o && setPontoPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="dp-ponto-preview">
          <DialogHeader>
            <DialogTitle>Importar horas do Ponto</DialogTitle>
            <DialogDescription>
              Confira os valores apurados no espelho de ponto antes de lançar como rubricas no holerite.
              Itens importados anteriormente serão substituídos.
            </DialogDescription>
          </DialogHeader>
          {pontoPreview && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-slate-500">
                Valor-hora base: <strong>{fmtBRL(pontoPreview.resumo?.valor_hora)}</strong> (divisor {pontoPreview.resumo?.divisor})
              </div>
              <table className="w-full text-sm border rounded-md overflow-hidden">
                <thead className="bg-slate-100 text-xs uppercase">
                  <tr><th className="text-left p-2">Rubrica</th><th className="text-right p-2">Ref.</th><th className="text-right p-2">Valor</th></tr>
                </thead>
                <tbody>
                  {(pontoPreview.itens || []).length === 0 ? (
                    <tr><td colSpan={3} className="p-3 text-center text-slate-500">Nenhuma verba apurada no ponto deste mês.</td></tr>
                  ) : (pontoPreview.itens || []).map((it, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">{it.rubrica_descricao}</td>
                      <td className="p-2 text-right text-xs text-slate-500">{it.referencia || '—'}</td>
                      <td className={`p-2 text-right ${it.tipo === 'desconto' ? 'text-red-700' : 'text-emerald-700'}`}>
                        {it.tipo === 'desconto' ? '- ' : ''}{fmtBRL(it.valor_desconto > 0 ? it.valor_desconto : it.valor_provento)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Banco de Horas — breakdown */}
              {pontoPreview.resumo?.banco_horas && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs space-y-1">
                  <div className="font-semibold text-amber-800">Banco de Horas</div>
                  <div className="flex justify-between text-amber-700">
                    <span>Saldo acumulado:</span>
                    <span>{pontoPreview.resumo.banco_horas.saldo_acumulado.toFixed(2)} h</span>
                  </div>
                  {pontoPreview.resumo.banco_horas.fechamento_periodo ? (
                    <>
                      {pontoPreview.resumo.banco_horas.saldo_a_pagar > 0 && (
                        <div className="flex justify-between text-emerald-700">
                          <span>Saldo a pagar na folha:</span>
                          <span className="font-semibold">{pontoPreview.resumo.banco_horas.saldo_a_pagar.toFixed(2)} h</span>
                        </div>
                      )}
                      {pontoPreview.resumo.banco_horas.saldo_a_descontar > 0 && (
                        <div className="flex justify-between text-red-700">
                          <span>Saldo a descontar na folha:</span>
                          <span className="font-semibold">{pontoPreview.resumo.banco_horas.saldo_a_descontar.toFixed(2)} h</span>
                        </div>
                      )}
                      <div className="text-amber-600 italic pt-1 border-t border-amber-200">
                        Mês de fechamento — saldo do banco será liquidado na folha
                      </div>
                    </>
                  ) : (
                    <div className="text-amber-600 italic">
                      Saldo sendo acumulado (fechamento em mês posterior)
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between text-xs text-slate-600">
                <span>Faltas: {pontoPreview.resumo?.faltas_atrasos?.dias_faltosos || 0} dia(s)</span>
                <span>Proventos: <strong className="text-emerald-700">{fmtBRL(pontoPreview.resumo?.total_proventos_ponto)}</strong> · Descontos: <strong className="text-red-700">{fmtBRL(pontoPreview.resumo?.total_descontos_ponto)}</strong></span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPontoPreview(null)} disabled={pontoLoading}>Cancelar</Button>
            <Button onClick={confirmarImportPonto} disabled={pontoLoading || !competenciaAberta} data-testid="dp-ponto-confirmar">
              {pontoLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Confirmar importação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: detalhamento do afastamento */}
      <Dialog open={afastModalOpen} onOpenChange={setAfastModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cálculo do Salário Base</DialogTitle>
            <DialogDescription>
              Detalhamento de como o valor do salário base foi calculado considerando o afastamento no período.
            </DialogDescription>
          </DialogHeader>
          {detalhe?.holerite?.afastamento_info && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b pb-1">
                <span className="text-slate-600">Salário base cheio</span>
                <span className="font-semibold">{fmtBRL(detalhe.holerite.afastamento_info.salario_base)}</span>
              </div>
              <div className="flex justify-between border-b pb-1">
                <span className="text-slate-600">Dias no mês</span>
                <span className="font-semibold">{detalhe.holerite.afastamento_info.dias_mes}</span>
              </div>
              <div className="flex justify-between border-b pb-1 text-amber-700">
                <span>Dias afastados</span>
                <span className="font-semibold">{detalhe.holerite.afastamento_info.dias_afastados}</span>
              </div>
              <div className="pl-4 flex justify-between border-b pb-1 text-emerald-700">
                <span>Primeiros 15 dias (pagos pela empresa)</span>
                <span className="font-semibold">{detalhe.holerite.afastamento_info.dias_pagos}</span>
              </div>
              <div className="pl-4 flex justify-between border-b pb-1 text-red-600">
                <span>Dias restantes (NÃO pagos)</span>
                <span className="font-semibold">{detalhe.holerite.afastamento_info.dias_nao_pagos}</span>
              </div>
              <div className="flex justify-between border-b pb-1 font-medium">
                <span>Dias considerados</span>
                <span>{detalhe.holerite.afastamento_info.dias_mes} — {detalhe.holerite.afastamento_info.dias_nao_pagos} = <strong>{detalhe.holerite.afastamento_info.dias_considerados}</strong></span>
              </div>
              <div className="flex justify-between pt-2 text-base font-bold text-indigo-700">
                <span>Valor do salário</span>
                <span>{fmtBRL(detalhe.holerite.afastamento_info.valor_salario)}</span>
              </div>
              <div className="text-xs text-slate-400 italic pt-2 border-t">
                {fmtBRL(detalhe.holerite.afastamento_info.salario_base)} ÷ {detalhe.holerite.afastamento_info.dias_mes} × {detalhe.holerite.afastamento_info.dias_considerados}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAfastModalOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUB-TAB: GESTÃO DE HOLERITES
// ============================================================================
const GestaoHoleritesTab = ({ onOpenCompetencia }) => {
  const [competencias, setCompetencias] = useState([]);
  const [compId, setCompId] = useState('');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const loadCompetencias = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/calculos-dp/competencias`, authHeader());
      const rows = r.data || [];
      setCompetencias(rows);
      if (!rows.length) {
        setLoading(false);
      }
      if (!compId && rows.length) {
        const preferred = rows.find((item) => item.status === 'fechada') || rows[0];
        setCompId(preferred.id);
      }
    } catch (e) {
      toast.error('Erro ao carregar competências para gestão');
      setLoading(false);
    }
  }, [compId]);

  const loadGestao = useCallback(async (selectedId) => {
    if (!selectedId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/competencias/${selectedId}/holerites/gestao`, authHeader());
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar gestão de holerites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCompetencias(); }, [loadCompetencias]);
  useEffect(() => { loadGestao(compId); }, [compId, loadGestao]);

  const downloadPdf = async (url, filename) => {
    try {
      const r = await axios.get(url, { ...authHeader(), responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao baixar PDF');
    }
  };

  const statusBadge = (situacao) => {
    if (situacao === 'assinado') return <Badge className="bg-emerald-600 text-white">Assinado</Badge>;
    if (situacao === 'validado') return <Badge className="bg-blue-600 text-white">Validado</Badge>;
    if (situacao === 'enviado') return <Badge variant="outline">Enviado</Badge>;
    if (situacao === 'recusado') return <Badge variant="destructive">Recusado</Badge>;
    return <Badge variant="secondary">Pendente</Badge>;
  };

  const fmtDateTime = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('pt-BR');
  };

  const resumo = data?.resumo || {};

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Gestão de Holerites</CardTitle>
          <CardDescription>Acompanhe por competência quem recebeu, validou e assinou cada holerite.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
            <div>
              <Label>Competência</Label>
              <Select value={compId} onValueChange={setCompId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma competência" />
                </SelectTrigger>
                <SelectContent>
                  {competencias.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {MESES[(c.mes || 1) - 1] || c.mes}/{c.ano} · {c.tipo?.replaceAll('_', ' ') || 'folha'} · {c.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {data?.competencia && (
              <Button variant="outline" onClick={() => onOpenCompetencia(data.competencia)}>
                <Eye className="h-4 w-4 mr-2" />Abrir holerites
              </Button>
            )}
          </div>

          <div className="grid gap-3 md:grid-cols-5">
            <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Total</div><div className="text-2xl font-semibold">{resumo.total || 0}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Enviados</div><div className="text-2xl font-semibold">{resumo.enviados || 0}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Validados</div><div className="text-2xl font-semibold">{resumo.validados || 0}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Assinados</div><div className="text-2xl font-semibold text-emerald-700">{resumo.assinados || 0}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Recusados</div><div className="text-2xl font-semibold text-red-700">{resumo.recusados || 0}</div></CardContent></Card>
          </div>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="p-3 text-left">Colaborador</th>
              <th className="p-3 text-right">Líquido</th>
              <th className="p-3 text-left">Situação</th>
              <th className="p-3 text-left">Canais</th>
              <th className="p-3 text-left">Datas</th>
              <th className="p-3 text-left">Auditoria</th>
              <th className="p-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr>
            ) : !(data?.holerites || []).length ? (
              <tr><td colSpan={7} className="p-4 text-center text-slate-500">Nenhum holerite nesta competência.</td></tr>
            ) : data.holerites.map((item) => (
              <tr key={item.id} className="border-t align-top hover:bg-slate-50">
                <td className="p-3">
                  <div className="font-medium">{item.employee_nome}</div>
                  <div className="text-xs text-slate-500">{item.employee_cargo || 'Sem cargo'}</div>
                </td>
                <td className="p-3 text-right font-medium">{fmtBRL(item.total_liquido)}</td>
                <td className="p-3">
                  <div className="space-y-1">
                    {statusBadge(item.situacao)}
                    {item.signature_request_status && <div className="text-xs text-slate-500">Portal: {item.signature_request_status}</div>}
                  </div>
                </td>
                <td className="p-3 text-xs text-slate-600">
                  <div>{item.email_destino || 'Sem e-mail'}</div>
                  <div>{item.whatsapp_destino || 'Sem WhatsApp'}</div>
                </td>
                <td className="p-3 text-xs text-slate-600">
                  <div>Enviado: {fmtDateTime(item.enviado_em)}</div>
                  <div>Assinado: {fmtDateTime(item.assinado_em)}</div>
                </td>
                <td className="p-3 text-xs text-slate-600">
                  <div>Req.: {item.signature_request_id ? item.signature_request_id.slice(0, 8) : '—'}</div>
                  <div>IP: {item.signature_ip || '—'}</div>
                  <div>Método: {item.signature_method || '—'}</div>
                  {item.recusa_observacoes && <div className="text-red-600">Recusa: {item.recusa_observacoes}</div>}
                </td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(`${API}/api/calculos-dp/holerites/${item.id}/pdf`, `holerite_${item.employee_nome || item.id}.pdf`)}>
                      <FileDown className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!item.pdf_assinado_disponivel}
                      onClick={() => downloadPdf(`${API}/api/calculos-dp/holerites/${item.id}/pdf-assinado`, `holerite_${item.employee_nome || item.id}_assinado.pdf`)}
                    >
                      <Receipt className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RelatoriosFolhaTab = () => {
  const reportOptions = [
    { value: 'resumo', label: 'Resumo folha' },
    { value: 'verbas', label: 'Verbas do mês' },
    { value: 'memoria', label: 'Memória mensal' },
    { value: 'ficha', label: 'Ficha financeira' },
  ];
  const [competencias, setCompetencias] = useState([]);
  const [competenciaId, setCompetenciaId] = useState('');
  const [tipo, setTipo] = useState('resumo');
  const [ano, setAno] = useState(new Date().getFullYear());
  const [mesInicio, setMesInicio] = useState(1);
  const [mesFim, setMesFim] = useState(12);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);

  const competenciaSel = competencias.find((c) => c.id === competenciaId);

  const loadCompetencias = useCallback(async () => {
    const r = await axios.get(`${API}/api/calculos-dp/competencias`, authHeader());
    const comps = (r.data || []).filter((c) => ['folha_mensal', 'adiantamento', '13_primeira', '13_segunda', 'ferias'].includes(c.tipo));
    setCompetencias(comps);
    if (!competenciaId && comps[0]) setCompetenciaId(comps[0].id);
  }, [competenciaId]);

  useEffect(() => {
    loadCompetencias().catch(() => toast.error('Erro ao carregar competências'));
  }, [loadCompetencias]);

  const gerar = async () => {
    if (tipo !== 'ficha' && !competenciaId) return toast.error('Selecione a competência');
    setLoading(true);
    try {
      let url = `${API}/api/calculos-dp/relatorios/resumo-folha?competencia_id=${competenciaId}`;
      if (tipo === 'verbas') url = `${API}/api/calculos-dp/relatorios/verbas?competencia_id=${competenciaId}`;
      if (tipo === 'memoria') url = `${API}/api/calculos-dp/relatorios/memoria-mensal?competencia_id=${competenciaId}`;
      if (tipo === 'ficha') url = `${API}/api/calculos-dp/relatorios/ficha-financeira?ano=${ano}&mes_inicio=${mesInicio}&mes_fim=${mesFim}`;
      const r = await axios.get(url, authHeader());
      setDados(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const baixarCsv = (nome, rows) => {
    if (!rows?.length) return toast.error('Não há linhas para exportar');
    const cols = Object.keys(rows[0]).filter((c) => typeof rows[0][c] !== 'object');
    const csv = [cols.join(';'), ...rows.map((row) => cols.map((c) => `"${String(row[c] ?? '').replaceAll('"', '""')}"`).join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${nome}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const linhasTabela = tipo === 'memoria' ? (dados?.pessoas || []) : tipo === 'ficha' ? (dados?.pessoas || []) : (dados?.linhas || []);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-600" />Relatórios</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {reportOptions.map((opt) => (
              <Button
                key={opt.value}
                type="button"
                size="sm"
                variant={tipo === opt.value ? 'default' : 'outline'}
                onClick={() => { setTipo(opt.value); setDados(null); }}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            {tipo !== 'ficha' ? (
              <div className="md:col-span-5">
                <Label>Competência</Label>
                <Select value={competenciaId} onValueChange={setCompetenciaId}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {competencias.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{MESES[(c.mes || 1) - 1]}/{c.ano} · {c.tipo?.replaceAll('_', ' ')} · {c.status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                <div className="md:col-span-2"><Label>Ano</Label><Input type="number" value={ano} onChange={(e) => setAno(Number(e.target.value || new Date().getFullYear()))} /></div>
                <div className="md:col-span-2"><Label>Mês inicial</Label><Input type="number" min="1" max="12" value={mesInicio} onChange={(e) => setMesInicio(Number(e.target.value || 1))} /></div>
                <div className="md:col-span-1"><Label>Mês final</Label><Input type="number" min="1" max="12" value={mesFim} onChange={(e) => setMesFim(Number(e.target.value || 12))} /></div>
              </>
            )}
            <Button onClick={gerar} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Calculator className="h-4 w-4 mr-1" />}Gerar</Button>
          </div>
        </CardContent>
      </Card>

      {dados && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base">
                  {reportOptions.find((opt) => opt.value === tipo)?.label}
                </CardTitle>
                <CardDescription>{tipo === 'ficha' ? `${mesInicio.toString().padStart(2, '0')}/${ano} a ${mesFim.toString().padStart(2, '0')}/${ano}` : `${competenciaSel ? `${MESES[(competenciaSel.mes || 1) - 1]}/${competenciaSel.ano}` : ''}`} · {linhasTabela.length} linha(s)</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => baixarCsv(`relatorio_folha_${tipo}`, tipo === 'ficha' ? linhasTabela.map((p) => ({ colaborador: p.employee_nome, ...p.totais })) : linhasTabela)}>
                <FileDown className="h-4 w-4 mr-1" />CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {dados.totais && (
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 text-sm">
                {Object.entries(dados.totais).map(([k, v]) => (
                  <div key={k} className="border rounded-md px-3 py-2">
                    <div className="text-xs text-slate-500 capitalize">{k.replaceAll('_', ' ')}</div>
                    <div className="font-semibold">{fmtBRL(v)}</div>
                  </div>
                ))}
              </div>
            )}

            {tipo === 'verbas' && (
              <table className="w-full text-sm border rounded">
                <thead className="bg-slate-100 text-xs uppercase"><tr><th className="text-left p-2">Cód.</th><th className="text-left p-2">Verba</th><th className="text-right p-2">Pessoas</th><th className="text-right p-2">Proventos</th><th className="text-right p-2">Descontos</th></tr></thead>
                <tbody>{linhasTabela.map((r, i) => <tr key={i} className="border-t"><td className="p-2 font-mono text-xs">{r.rubrica_codigo}</td><td className="p-2">{r.rubrica_descricao}</td><td className="p-2 text-right">{r.quantidade_pessoas}</td><td className="p-2 text-right text-emerald-700">{fmtBRL(r.total_proventos)}</td><td className="p-2 text-right text-red-700">{fmtBRL(r.total_descontos)}</td></tr>)}</tbody>
              </table>
            )}

            {tipo !== 'verbas' && (
              <table className="w-full text-sm border rounded">
                <thead className="bg-slate-100 text-xs uppercase"><tr><th className="text-left p-2">Colaborador</th><th className="text-right p-2">Proventos</th><th className="text-right p-2">Descontos</th><th className="text-right p-2">IRRF</th><th className="text-right p-2">Conv. médico</th><th className="text-right p-2">Líquido</th></tr></thead>
                <tbody>{linhasTabela.map((r, i) => {
                  const row = r.totais ? { employee_nome: r.employee_nome, ...r.totais } : r;
                  return <tr key={i} className="border-t"><td className="p-2">{row.employee_nome}</td><td className="p-2 text-right text-emerald-700">{fmtBRL(row.proventos)}</td><td className="p-2 text-right text-red-700">{fmtBRL(row.descontos)}</td><td className="p-2 text-right text-red-700">{fmtBRL(row.irrf)}</td><td className="p-2 text-right text-red-700">{fmtBRL(row.convenio_medico)}</td><td className="p-2 text-right font-semibold">{fmtBRL(row.liquido)}</td></tr>;
                })}</tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
const FolhaPagamentoTab = () => {
  const [activeSub, setActiveSub] = useState('rubricas');
  const [compSel, setCompSel] = useState(null);

  return (
    <Tabs value={activeSub} onValueChange={setActiveSub} className="w-full">
      <TabsList className="grid grid-cols-2 md:grid-cols-5 mb-4">
        <TabsTrigger value="rubricas" data-testid="dp-folha-tab-rubricas"><FileText className="h-4 w-4 mr-1" />Rubricas</TabsTrigger>
        <TabsTrigger value="competencias" data-testid="dp-folha-tab-competencias"><Calendar className="h-4 w-4 mr-1" />Competências</TabsTrigger>
        <TabsTrigger value="holerites" data-testid="dp-folha-tab-holerites"><Calculator className="h-4 w-4 mr-1" />Holerites</TabsTrigger>
        <TabsTrigger value="gestao" data-testid="dp-folha-tab-gestao"><Receipt className="h-4 w-4 mr-1" />Gestão</TabsTrigger>
        <TabsTrigger value="relatorios" data-testid="dp-folha-tab-relatorios"><FileText className="h-4 w-4 mr-1" />Relatórios</TabsTrigger>
      </TabsList>
      <TabsContent value="rubricas"><RubricasTab /></TabsContent>
      <TabsContent value="competencias">
        <CompetenciasTab onOpenHolerites={(c) => { setCompSel(c); setActiveSub('holerites'); }} />
      </TabsContent>
      <TabsContent value="holerites">
        <HoleritesTab competencia={compSel} onBack={() => setActiveSub('competencias')} />
      </TabsContent>
      <TabsContent value="gestao">
        <GestaoHoleritesTab onOpenCompetencia={(c) => { setCompSel(c); setActiveSub('holerites'); }} />
      </TabsContent>
      <TabsContent value="relatorios"><RelatoriosFolhaTab /></TabsContent>
    </Tabs>
  );
};

export default FolhaPagamentoTab;
